import { useEffect, useRef } from 'react';
import { ControlItemValue, StaticControlItem } from '../../../../types/types';

export const useDefaultOptionValue = (
  options: StaticControlItem[] | undefined,
  value: ControlItemValue | null,
  defaultValue: ControlItemValue | undefined,
  onChange: (value: ControlItemValue, propagateHandler?: boolean) => void,
  isColumnFilter?: boolean
): void => {
  // Column filters must only auto-fill once per popover mount, so a later clear-to-empty (Zrušit) isn't fought and re-applied.
  const hasAppliedRef = useRef(false);
  // Last default written by this hook. When the write doesn't come back as a
  // valid value (a consumer dropping/transforming it), retrying the same write
  // would loop forever ("maximum update depth").
  const lastAppliedRef = useRef<ControlItemValue | undefined>(undefined);

  useEffect(() => {
    if (!options?.length) return;
    if (isColumnFilter && hasAppliedRef.current) return;

    // A value listed among the options is a valid choice even when it is
    // "empty" — definitions may offer an explicit "none" option
    // ({ value: '' }); auto-filling would fight it forever.
    if (options.some((o) => o.value === value)) {
      hasAppliedRef.current = true;
      // A valid value arrived — a future clear may auto-fill again.
      lastAppliedRef.current = undefined;
      return;
    }

    hasAppliedRef.current = true;
    let target = options[0];
    if (defaultValue !== undefined) {
      const match = options.find((o) => o.value === defaultValue);
      if (match) target = match;
    }

    if (lastAppliedRef.current === target.value) {
      console.warn(
        '[useDefaultOptionValue] applied default did not come back as a valid value — not retrying',
        { target: target.value, value, options }
      );
      return;
    }

    lastAppliedRef.current = target.value;
    // Programmatic write (mount-time default) — propagateHandler=false so
    // consumers can tell it apart from a user-initiated change.
    onChange(target.value, false);
  }, [defaultValue, value, options, onChange, isColumnFilter]);
};
